import classNames from "classnames";
import { type ReactNode, useRef } from "react";
import type {
    GroupBase,
    Props as ReactSelectProps,
    SelectComponentsConfig,
} from "react-select";
import ReactSelect from "react-select";

interface SelectOption {
    label: string;
    value: unknown;
}

interface SelectProps {
    /**
     * Whether the field is required.
     */
    required?: boolean;
    /**
     * Whether multiple selections are allowed.
     */
    multi?: boolean;
    /**
     * Whether the dropdown is searchable.
     */
    searchable?: boolean;
    /**
     * The placeholder text.
     */
    placeholder?: string;
    /**
     * The array of selectable options.
     */
    options: SelectOption[];
    /**
     * The selection change handler.
     */
    onChange?: ReactSelectProps<
        SelectOption,
        boolean,
        GroupBase<SelectOption>
    >["onChange"];
    /**
     * The current selected value.
     */
    value?: string | number | SelectOption[];
    /**
     * Custom react-select component overrides.
     */
    components?: SelectComponentsConfig<
        SelectOption,
        boolean,
        GroupBase<SelectOption>
    >;
    /**
     * Additional CSS class name.
     */
    className?: string;
    /**
     * The CSS class name prefix for styling.
     */
    classNamePrefix?: string;
}

/**
 * Wrapper around react-select with required field and clearable support.
 *
 * @param {SelectProps} props - The component props.
 * @returns {ReactNode} The rendered select component.
 */
const Select = ({
    required = false,
    multi = false,
    searchable = false,
    placeholder = undefined,
    options,
    onChange,
    value: valueProp,
    components = undefined,
    className: classNameProp,
    classNamePrefix,
}: SelectProps): ReactNode => {
    const selectRef = useRef(undefined);

    const onKeyUp = (e) => {
        if (!multi && !searchable && !required && e.keyCode === 8) {
            selectRef.current.select.selectOption("");
        }
    };

    let resolvedValue: SelectOption | SelectOption[] | undefined;
    let className = classNameProp;

    if (!multi) {
        resolvedValue = options.find((o) => o.value === valueProp);
    } else {
        resolvedValue = options.filter(
            (o) =>
                valueProp &&
                Array.isArray(valueProp) &&
                valueProp.some((v) => v.value === o.value),
        );
    }

    if (!className) {
        if (multi) {
            className = "basic-multi-select";
        } else {
            className = "basic-single";
        }
    }

    const customStyles = {
        control: (base) => ({
            ...base,
            boxShadow: "none",
        }),
    };

    return (
        <div onKeyUp={onKeyUp} className="lights-map-select-container">
            <ReactSelect
                placeholder={placeholder}
                components={components}
                options={options}
                onChange={onChange}
                ref={selectRef}
                value={resolvedValue}
                isMulti={multi}
                styles={customStyles}
                isSearchable={searchable || multi}
                className={classNames("lights-map-select", className)}
                classNamePrefix={classNamePrefix ? classNamePrefix : "select"}
            />
        </div>
    );
};

export default Select;
